import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { HerracContext } from '../../App';
import { useNotifications } from '../services/useNotifications';
import NotificationService, {
    getNotificationIcon,
    getNotificationColor,
    getNotificationLabel,
    formatNotificationDate
} from '../services/NotificationService';
import {
    FaBell,
    FaCheck,
    FaTrash,
    FaFilter,
    FaGavel,
    FaCheckDouble,
    FaSpinner,
    FaWifi,
    FaExclamationTriangle,
    FaSync
} from 'react-icons/fa';
import { toast } from 'react-toastify';

function CabinetNotifications() {
    const navigate = useNavigate();
    const { user } = useContext(HerracContext);

    // useNotifications hook
    const {
        notifications,
        unreadCount,
        loading,
        error,
        isConnected,
        connectionState,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAll,
        requestNotificationPermission
    } = useNotifications({
        onNewNotification: (notification) => {
            toast.info(`${getNotificationIcon(notification.type)} ${notification.title}`, {
                onClick: () => {
                    if (notification.relatedAuctionId) {
                        navigate(`/elanlar/herrac/${notification.relatedAuctionId}`);
                    }
                }
            });
        }
    });

    // Local state
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showFilters, setShowFilters] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Redirect if not logged in
    useEffect(() => {
        if (!user) {
            navigate('/giris');
        }
    }, [user, navigate]);

    // Request browser notification permission
    useEffect(() => {
        requestNotificationPermission();
    }, [requestNotificationPermission]);

    // Handlers
    const handleMarkAsRead = useCallback(async (notificationId) => {
        const result = await markAsRead(notificationId);
        if (!result.success) {
            toast.error('Xəta baş verdi');
        }
    }, [markAsRead]);

    const handleMarkAllAsRead = useCallback(async () => {
        setActionLoading(true);
        const result = await markAllAsRead();
        setActionLoading(false);
        
        if (result.success) {
            toast.success('Bütün bildirişlər oxundu işarə edildi');
        } else {
            toast.error('Xəta baş verdi');
        }
    }, [markAllAsRead]);

    const handleDeleteNotification = useCallback(async (notificationId) => {
        if (!window.confirm('Bu bildirişi silmək istədiyinizə əminsiniz?')) {
            return;
        }

        const result = await deleteNotification(notificationId);
        if (result.success) {
            toast.success('Bildiriş silindi');
        } else {
            toast.error('Xəta baş verdi');
        }
    }, [deleteNotification]);

    const handleClearAll = useCallback(async () => {
        if (!window.confirm('Bütün bildirişləri silmək istədiyinizə əminsiniz?')) {
            return;
        }

        setActionLoading(true);
        const result = await clearAll();
        setActionLoading(false);

        if (result.success) {
            toast.success('Bütün bildirişlər silindi');
        } else {
            toast.error('Xəta baş verdi');
        }
    }, [clearAll]);

    const handleNotificationClick = useCallback((notification) => {
        if (!notification.isRead) {
            handleMarkAsRead(notification.id);
        }

        if (notification.relatedAuctionId) {
            navigate(`/elanlar/herrac/${notification.relatedAuctionId}`);
        }
    }, [handleMarkAsRead, navigate]);

    const handleRefresh = useCallback(() => {
        fetchNotifications(true);
    }, [fetchNotifications]);

    // Filter notifications
    const filteredNotifications = notifications
        .filter((n) => {
            if (filterType !== 'all' && n.type !== filterType) return false;
            if (filterStatus === 'unread' && n.isRead) return false;
            if (filterStatus === 'read' && !n.isRead) return false;
            return true;
        })
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    // Connection status badge
    const getConnectionBadge = () => {
        switch (connectionState) {
            case 'connected':
                return <span className="connection-badge connection-badge--connected"><FaWifi /> Canlı</span>;
            case 'connecting':
            case 'reconnecting':
                return <span className="connection-badge connection-badge--connecting"><FaSpinner className="spin" /> Qoşulur...</span>;
            default:
                return <span className="connection-badge connection-badge--disconnected"><FaExclamationTriangle /> Offline</span>;
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="cabinet-notifications">
                <div className="cabinet-notifications__loading">
                    <FaSpinner className="cabinet-notifications__spinner" />
                    <p>Bildirişlər yüklənir...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="cabinet-notifications">
                <div className="cabinet-notifications__error">
                    <FaExclamationTriangle />
                    <p>{error}</p>
                    <button onClick={handleRefresh}>Yenidən cəhd et</button>
                </div>
            </div>
        );
    }

    return (
        <div className="cabinet-notifications">
            {/* Header */}
            <div className="cabinet-notifications__header">
                <div className="cabinet-notifications__header-content">
                    <h2 className="cabinet-notifications__title">
                        <FaBell /> Bildirişlər
                    </h2>
                    {unreadCount > 0 && (
                        <span className="cabinet-notifications__badge">{unreadCount}</span>
                    )}
                    {getConnectionBadge()}
                </div>
                <p className="cabinet-notifications__subtitle">
                    Toplam {filteredNotifications.length} bildiriş
                </p>
            </div>

            {/* Toolbar */}
            <div className="cabinet-notifications__toolbar">
                <div className="cabinet-notifications__actions">
                    <button
                        className="cabinet-notifications__btn cabinet-notifications__btn--refresh"
                        onClick={handleRefresh}
                        disabled={loading}
                        title="Yenilə"
                    >
                        <FaSync className={loading ? 'spin' : ''} />
                    </button>
                    
                    {unreadCount > 0 && (
                        <button
                            className="cabinet-notifications__btn cabinet-notifications__btn--primary"
                            onClick={handleMarkAllAsRead}
                            disabled={actionLoading}
                            title="Hamısını oxunmuş işarə et"
                        >
                            <FaCheckDouble /> Hamısını Oxu
                        </button>
                    )}
                    
                    {notifications.length > 0 && (
                        <button
                            className="cabinet-notifications__btn cabinet-notifications__btn--danger"
                            onClick={handleClearAll}
                            disabled={actionLoading}
                            title="Hamısını sil"
                        >
                            <FaTrash /> Hamısını Sil
                        </button>
                    )}
                </div>

                <button
                    className={`cabinet-notifications__filter-btn ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <FaFilter /> Filtr
                </button>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="cabinet-notifications__filters">
                    <div className="filter-group">
                        <label>Tip</label>
                        <div className="filter-options">
                            {[
                                { value: 'all', label: '📋 Hamısı' },
                                { value: 'bid_outbid', label: '🔔 Təklif Keçildi' },
                                { value: 'auction_won', label: '🏆 Qazandınız' },
                                { value: 'auction_ended', label: '⏰ Bitdi' },
                                { value: 'deposit_refunded', label: '💰 Qaytarıldı' },
                                { value: 'new_bid', label: '📈 Yeni Təklif' },
                                { value: 'system', label: 'ℹ️ Sistem' },
                            ].map((opt) => (
                                <button
                                    key={opt.value}
                                    className={`filter-option ${filterType === opt.value ? 'active' : ''}`}
                                    onClick={() => setFilterType(opt.value)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="filter-group">
                        <label>Status</label>
                        <div className="filter-options">
                            {[
                                { value: 'all', label: '📋 Hamısı' },
                                { value: 'unread', label: '📬 Oxunmamış' },
                                { value: 'read', label: '📭 Oxunmuş' },
                            ].map((opt) => (
                                <button
                                    key={opt.value}
                                    className={`filter-option ${filterStatus === opt.value ? 'active' : ''}`}
                                    onClick={() => setFilterStatus(opt.value)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Notifications List */}
            {filteredNotifications.length === 0 ? (
                <div className="cabinet-notifications__empty">
                    <FaBell className="cabinet-notifications__empty-icon" />
                    <h3>Bildiriş yoxdur</h3>
                    <p>
                        {notifications.length === 0
                            ? 'Hələ bildiriş almamısınız'
                            : 'Bu filtrə uyğun bildiriş yoxdur'}
                    </p>
                </div>
            ) : (
                <div className="cabinet-notifications__list">
                    {filteredNotifications.map((notification) => (
                        <div
                            key={notification.id}
                            className={`notification-item notification-item--${getNotificationColor(notification.type)} ${!notification.isRead ? 'unread' : ''}`}
                            onClick={() => handleNotificationClick(notification)}
                        >
                            <div className="notification-item__icon">
                                <span className="notification-item__emoji">
                                    {getNotificationIcon(notification.type)}
                                </span>
                            </div>

                            <div className="notification-item__content">
                                <div className="notification-item__header">
                                    <h4 className="notification-item__title">
                                        {notification.title || getNotificationLabel(notification.type)}
                                    </h4>
                                    <span className="notification-item__time">
                                        {formatNotificationDate(notification.createdAt)}
                                    </span>
                                </div>
                                <p className="notification-item__message">{notification.message}</p>
                                {notification.relatedAuctionTitle && (
                                    <div className="notification-item__auction">
                                        <FaGavel /> {notification.relatedAuctionTitle}
                                    </div>
                                )}
                            </div>

                            <div className="notification-item__actions">
                                {!notification.isRead && (
                                    <button
                                        className="notification-item__btn notification-item__btn--read"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleMarkAsRead(notification.id);
                                        }}
                                        title="Oxunmuş işarə et"
                                    >
                                        <FaCheck />
                                    </button>
                                )}
                                <button
                                    className="notification-item__btn notification-item__btn--delete"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteNotification(notification.id);
                                    }}
                                    title="Sil"
                                >
                                    <FaTrash />
                                </button>
                            </div>

                            {!notification.isRead && (
                                <div className="notification-item__indicator" />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default CabinetNotifications;